import { FC, useRef, useState } from 'react';
import { SCUIContent } from '../../atomic/organisms';
import { useTranslation } from 'react-i18next';
import { Box, FormControl, FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIButton, SCUILoadingLinear } from '../../atomic/atoms';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { CustomerCommand } from '../../services/command';
import Swal from 'sweetalert2';
import ForcePasswordResetModal from './component/ForcePasswordResetModal';
import { CPWC } from './customerPasswordChange.classes';

export const CustomerPasswordChange: FC<any> = () => {
    const { t } = useTranslation();
    const [searchStatus, setSearchStatus] = useState(1);
    const [text, setText] = useState<any>('');
    const [serviceResponse, setServiceResponse] = useState();
    const [loading, setLoading] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const handleChange = (event: any) => {
        setSearchStatus(event.target.value);
    };
    const ForcePasswordReset = async () => {
        setLoading(true);

        const trimmedLines = text
            ?.split('\n')
            .map((line: any) => line.trim())
            .filter((line: any) => line !== '');
        let params: any = {
            Emails: [],
            PhoneNumbers: [],
        };

        if (searchStatus === 1) {
            params.Emails = trimmedLines;
        } else {
            params.PhoneNumbers = trimmedLines;
        }

        if (trimmedLines.length > 0) {
            if (trimmedLines?.length > 200) {
                Swal.fire(t('customerPasswordChange.limitMessage'), '', 'error');
                setLoading(false);
                return;
            }
            CustomerCommand.ForcePasswordReset(params)
                .then((res: any) => {
                    if (res) {
                        setServiceResponse(res);
                        setOpen(true);
                    } else {
                        Swal.fire(t('general.error'), '', 'error');
                    }
                })
                .catch(() => {
                    Swal.fire(t('general.error'), '', 'error');
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            Swal.fire(t('mobilePanel.noEmpty'), '', 'error');
            setLoading(false);
        }
    };
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const lineNumberRef = useRef<HTMLDivElement | null>(null);
    const lines = text.split('\n');
    const handleScroll = () => {
        if (textareaRef.current && lineNumberRef.current) {
            lineNumberRef.current.scrollTop = textareaRef.current.scrollTop;
        }
    };
    return (
        <SCUIContent>
            <Box className={CPWC.page}>
            <Grid className={CPWC.breadcrumbRow} container direction="row" spacing={2}>
                <SCUIBreadCrumb
                    title={t('customerPasswordChange.title')}
                    componentLink={'forcePasswordReset'}
                    onClick={() => {
                        setText('');
                    }}
                />
            </Grid>
            <SCUILoadingLinear display={loading} />
            <Grid
                size={{
                    xl: 12,
                    lg: 12,
                    md: 12,
                    xs: 12
                }}>
                <FormControl className={CPWC.fieldSearchMode} component="fieldset">
                    <RadioGroup className={CPWC.inputRadioGroupSearchMode} row value={searchStatus} onChange={handleChange}>
                        <FormControlLabel
                            className={CPWC.inputRadioOptionEmail}
                            value={1}
                            control={<Radio />}
                            label={t('customerPasswordChange.emailSearch')}
                        />
                        <FormControlLabel
                            className={CPWC.inputRadioOptionPhone}
                            value={2}
                            control={<Radio />}
                            label={t('customerPasswordChange.phoneNumberSearch')}
                        />
                    </RadioGroup>
                </FormControl>
            </Grid>
            <Grid
                size={{
                    xl: 12,
                    lg: 12,
                    md: 12,
                    xs: 12
                }}>
                <Box
                    className={CPWC.editorWrap}
                    sx={{
                        display: 'flex',
                        width: 700,
                        height: 400,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        position: 'relative',
                    }}
                >
                    <Box
                        className={CPWC.lineNumbersColumn}
                        ref={lineNumberRef}
                        sx={{
                            backgroundColor: '#f4f4f4',
                            padding: '8px 4px',
                            color: '#999',
                            textAlign: 'right',
                            userSelect: 'none',
                            width: '40px',
                            lineHeight: '20px',
                            overflow: 'hidden',
                        }}
                    >
                        {lines.map((line: any, index: any) => (
                            <div key={`${index}-${line}`}>{index + 1}</div>
                        ))}
                    </Box>

                    <textarea
                        className={CPWC.inputBulkLines}
                        ref={textareaRef}
                        onScroll={handleScroll}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        spellCheck={false}
                        style={{
                            flex: 1,
                            padding: '8px',
                            border: 'none',
                            resize: 'none',
                            outline: 'none',
                            lineHeight: '20px',
                            fontFamily: 'inherit',
                            height: '100%',
                            overflow: 'auto',
                        }}
                    />
                </Box>

                <ForcePasswordResetModal open={open} onClose={() => setOpen(false)} resultData={serviceResponse} />

                <div
                    className={CPWC.actionsRow}
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '12px',
                        maxWidth: 700,
                        minWidth: 700,
                        marginTop: '1rem',
                    }}
                >
                    <div style={{ position: 'relative', zIndex: 3 }}>
                        <SCUIButton
                            className={CPWC.inputButtonClear}
                            text={t('general.clear')}
                            type="error"
                            buttonType="submit"
                            sizeType="default"
                            size="m"
                            style={{ width: 110, borderRadius: '4px' }}
                            startIcon={<DeleteIcon style={{ color: '#ffffff' }} />}
                            onClick={() => {
                                setText('');
                            }}
                        />
                    </div>
                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <SCUIButton
                            className={CPWC.inputButtonSubmit}
                            text={t('general.search')}
                            type="primary"
                            buttonType="submit"
                            sizeType="default"
                            size="m"
                            style={{ width: 110, borderRadius: '4px' }}
                            startIcon={<SearchIcon style={{ color: '#ffffff' }} />}
                            onClick={() => ForcePasswordReset()}
                        />
                    </div>
                </div>
            </Grid>
            </Box>
        </SCUIContent>
    );
};
